import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, Sparkles, Check, Undo2, Flame } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { useAuth } from "../../AuthContext";

/** McpProRequestsCard — Beta PRO access requests (handled manually). */
export default function McpProRequestsCard({ dm }) {
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [granting, setGranting] = useState(null);
  const [spots, setSpots] = useState(null);
  const [totalEdit, setTotalEdit] = useState("");
  const [savingTotal, setSavingTotal] = useState(false);
  const { authHeader } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/mcp-pro-requests?limit=50`, { headers: authHeader() });
      setRows(data.requests || []);
      setPending(data.pending || 0);
      const s = await axios.get(`${API}/mcp/beta-spots`);
      setSpots(s.data);
      setTotalEdit(String(s.data.total));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const saveTotal = async () => {
    const t = parseInt(totalEdit, 10);
    if (!t || t < 1) { toast.error("Total must be a positive number"); return; }
    setSavingTotal(true);
    try {
      const { data } = await axios.put(`${API}/admin/mcp-beta-spots`, { total: t }, { headers: authHeader() });
      setSpots(data);
      setTotalEdit(String(data.total));
      toast.success(`Beta spots total set to ${data.total} — ${data.remaining} left`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update total");
    } finally {
      setSavingTotal(false);
    }
  };

  const markHandled = async (id) => {
    try {
      await axios.patch(`${API}/admin/mcp-pro-requests/${id}`, { status: "handled" }, { headers: authHeader() });
      setRows(prev => prev.map(r => (r.id === id ? { ...r, status: "handled" } : r)));
      setPending(p => Math.max(0, p - 1));
      toast.success("Marked as handled — remember to upgrade the key tier if approved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update request");
    }
  };

  const grantPro = async (id) => {
    setGranting(id);
    try {
      const { data } = await axios.post(`${API}/admin/mcp-pro-requests/${id}/grant`, {}, { headers: authHeader() });
      setRows(prev => prev.map(r => (r.id === id ? { ...r, status: "granted" } : r)));
      setPending(p => Math.max(0, p - 1));
      toast.success(data.message || "PRO granted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to grant PRO");
    } finally {
      setGranting(null);
    }
  };

  const revokeBeta = async (id) => {
    setGranting(id);
    try {
      const { data } = await axios.post(`${API}/admin/mcp-pro-requests/${id}/revoke`, {}, { headers: authHeader() });
      setRows(prev => prev.map(r => (r.id === id ? { ...r, status: "revoked" } : r)));
      toast.success(data.message || "Beta PRO revoked");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke");
    } finally {
      setGranting(null);
    }
  };

  const card = `rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
  const cardShadow = { boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" };

  return (
    <div className={card} style={cardShadow} data-testid="mcp-pro-requests-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className={`text-sm font-semibold flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
          <Sparkles className="w-4 h-4 text-purple-500" /> PRO Beta Requests
        </h3>
        <div className="flex items-center gap-2">
          {pending > 0 && (
            <span data-testid="mcp-pro-requests-pending" className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${dm ? "bg-purple-400/15 text-purple-300" : "bg-purple-100 text-purple-700"}`}>
              {pending} pending
            </span>
          )}
          <button onClick={load} data-testid="mcp-pro-requests-refresh-btn"
            className={`p-1.5 rounded-lg transition-colors ${dm ? "text-slate-500 hover:bg-white/5" : "text-[#6b7a7d] hover:bg-slate-100"}`} title="Refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {spots && (
        <div data-testid="mcp-beta-spots-admin"
          className={`flex items-center gap-2 flex-wrap mb-4 px-3 py-2 rounded-lg text-xs ${dm ? "bg-purple-400/5" : "bg-purple-50/70"}`}>
          <Flame className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
          <span className={dm ? "text-purple-300" : "text-purple-700"}>
            <strong>{spots.taken}</strong> taken · <strong>{spots.remaining}</strong> of <strong>{spots.total}</strong> Beta spots left
          </span>
          <span className={`ml-auto flex items-center gap-1.5 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Total:
            <input type="number" min={1} max={10000} value={totalEdit} onChange={e => setTotalEdit(e.target.value)}
              data-testid="mcp-beta-spots-total-input"
              className={`w-16 px-2 py-1 rounded-md text-xs outline-none ${dm ? "bg-[#0c1011] text-slate-200" : "bg-white text-[#191c1d] border border-slate-200"}`} />
            <button onClick={saveTotal} disabled={savingTotal || String(spots.total) === totalEdit}
              data-testid="mcp-beta-spots-save-btn"
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors disabled:opacity-40 ${dm ? "bg-purple-400/15 text-purple-300 hover:bg-purple-400/25" : "bg-purple-100 text-purple-700 hover:bg-purple-200"}`}>
              {savingTotal ? "Saving…" : "Save"}
            </button>
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
        </div>
      ) : rows.length === 0 ? (
        <p className={`text-xs text-center py-6 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
          No Beta PRO requests yet. Requests from /mcp, /pro and the key portal appear here.
        </p>
      ) : (
        <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
          {rows.map((r) => (
            <div key={r.id} data-testid={`mcp-pro-request-row-${r.id}`}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs ${dm ? "bg-[#0c1011]" : "bg-[#f3f4f5]"} ${r.status === "handled" ? "opacity-50" : ""}`}>
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase flex-shrink-0 ${
                r.status === "new"
                  ? (dm ? "bg-purple-400/15 text-purple-300" : "bg-purple-100 text-purple-700")
                  : r.status === "granted"
                    ? (dm ? "bg-emerald-400/15 text-emerald-300" : "bg-emerald-100 text-emerald-700")
                    : (dm ? "bg-slate-700 text-slate-400" : "bg-slate-200 text-slate-500")
              }`}>
                {r.status}
              </span>
              <span className={`font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{r.name || "—"}</span>
              <span className={`truncate ${dm ? "text-slate-400" : "text-[#3b494c]"}`}>{r.email}</span>
              <span className={`truncate flex-1 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                {r.company ? `${r.company} · ` : ""}{r.message || ""}
              </span>
              <span className={`flex-shrink-0 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                {r.created_at ? new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
              </span>
              {r.status === "new" && (
                <>
                  <button onClick={() => grantPro(r.id)} disabled={granting === r.id}
                    data-testid={`mcp-pro-request-grant-${r.id}`}
                    className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-colors disabled:opacity-50 ${dm ? "bg-purple-400/10 text-purple-300 hover:bg-purple-400/20" : "bg-purple-50 text-purple-700 hover:bg-purple-100"}`}>
                    <Sparkles className={`w-3 h-3 ${granting === r.id ? "animate-pulse" : ""}`} />
                    {granting === r.id ? "Granting…" : "Grant PRO"}
                  </button>
                  <button onClick={() => markHandled(r.id)} data-testid={`mcp-pro-request-handle-${r.id}`}
                    className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-colors ${dm ? "bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}>
                    <Check className="w-3 h-3" /> Handled
                  </button>
                </>
              )}
              {r.status === "granted" && (
                <button onClick={() => revokeBeta(r.id)} disabled={granting === r.id}
                  data-testid={`mcp-pro-request-revoke-${r.id}`}
                  className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-colors disabled:opacity-50 ${dm ? "bg-rose-400/10 text-rose-300 hover:bg-rose-400/20" : "bg-rose-50 text-rose-700 hover:bg-rose-100"}`}>
                  <Undo2 className="w-3 h-3" />
                  {granting === r.id ? "Revoking…" : "Revoke"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
